// ResponseUI/ResponseTemplateEditor.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import nunjucks from "nunjucks/browser/nunjucks";
import styles from "./ResponseTemplateEditor.module.css";

nunjucks.configure({ autoescape: false });

function flattenObject(obj, prefix = "") {
  let result = {};
  for (let key in obj) {
    if (!obj.hasOwnProperty(key)) continue;
    const newKey = prefix ? `${prefix}.${key}` : key;
    const val = obj[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      Object.assign(result, flattenObject(val, newKey));
    } else {
      result[newKey] = val;
    }
  }
  return result;
}

const ResponseTemplateEditor = ({
  initialSampleMap = {},
  initialTemplate = {},
  onChange,
  onSampleMapChange,
  placeholder = "✅ Available rooms:\n{% for room in response_data %}\n• {{ room.room_type }} – ${{ room.price_per_night }}\n{% endfor %}",
  readOnly = false,
  appData = null,
  onReqUidSelect = null,
  configId = null,
  kbId = null,
  respData = {},
  activeStatus: propActiveStatus = null,
  onViewAllTemplates = null,
  onTemplateCreated = null,
  onActiveStatusChange = null,

  // When true: only the status tabs and the Sample Response textarea are
  // rendered. All template-design UI (Insert field, saved-template dropdown,
  // View All Templates, req_uid indicator, helper text) is hidden, and the
  // template-sync / template-notify effects are skipped. Used by
  // Authentication Configure, where the backend renders a fixed default
  // template and the user never edits one.
  hideTemplateDesigner = false,
}) => {
  const normalizeTemplate = (tpl) => {
    if (!tpl || typeof tpl !== "object") return {};
    return { ...tpl };
  };

  const getSampleMapFromProps = (prop) => {
    if (!prop || typeof prop !== "object") return {};
    const result = {};
    for (const key of Object.keys(prop)) {
      const value = prop[key];
      result[key] = typeof value === "string" ? value : JSON.stringify(value);
    }
    return result;
  };

  // State
  const [sampleMap, setSampleMap] = useState(() =>
    getSampleMapFromProps(initialSampleMap),
  );
  const [templateObject, setTemplateObject] = useState(() => {
    const tpl = normalizeTemplate(initialTemplate);
    if (
      Object.keys(tpl).length === 0 &&
      Object.keys(initialSampleMap).length > 0
    ) {
      const newTpl = {};
      Object.keys(initialSampleMap).forEach((key) => {
        newTpl[key] = "";
      });
      return newTpl;
    }
    return tpl;
  });
  const [preview, setPreview] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [parsedSample, setParsedSample] = useState(null);

  // State for req_uid selection and final responses list
  const [finalResponses, setFinalResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [selectedReqUid, setSelectedReqUid] = useState("");
  const [reqUidDropdownOpen, setReqUidDropdownOpen] = useState(false);

  // Get first status or use prop active status
  const getFirstStatus = () => {
    if (propActiveStatus) return propActiveStatus;
    if (Object.keys(templateObject).length > 0) {
      return Object.keys(templateObject)[0];
    }
    if (Object.keys(sampleMap).length > 0) {
      return Object.keys(sampleMap)[0];
    }
    return "";
  };

  const [internalActiveStatus, setInternalActiveStatus] = useState(getFirstStatus());
  const activeStatus = propActiveStatus || internalActiveStatus;

  const isProgrammaticUpdate = useRef(false);
  const isSampleProgrammaticUpdate = useRef(false);
  const reqUidDropdownRef = useRef(null);

  // Always-fresh refs for callbacks — prevents the notify effects from
  // re-firing on every parent render (the parent creates a new closure each
  // time).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onSampleMapChangeRef = useRef(onSampleMapChange);
  onSampleMapChangeRef.current = onSampleMapChange;

  // Sync sample map from parent
  useEffect(() => {
    const newSampleMap = getSampleMapFromProps(initialSampleMap);
    const currentStr = JSON.stringify(sampleMap);
    const newStr = JSON.stringify(newSampleMap);
    if (currentStr !== newStr) {
      isSampleProgrammaticUpdate.current = true;
      setSampleMap(newSampleMap);
    }
  }, [initialSampleMap]);

  // Notify parent when the local sample map changes (user edits).
  useEffect(() => {
    if (onSampleMapChangeRef.current && !isSampleProgrammaticUpdate.current) {
      onSampleMapChangeRef.current(sampleMap);
    }
    isSampleProgrammaticUpdate.current = false;
  }, [sampleMap]);

  // Template sync from parent — additive + "adopt non-empty" semantics.
  // Skipped when hideTemplateDesigner is true (auth configs never edit the
  // template).
  useEffect(() => {
    if (hideTemplateDesigner) return;

    const normalized = normalizeTemplate(initialTemplate);
    const sampleKeys = new Set(Object.keys(sampleMap));
    const updates = {};
    let changed = false;

    for (const key of sampleKeys) {
      const localVal = templateObject[key];
      const propVal = normalized[key];

      if (localVal === undefined) {
        updates[key] = propVal !== undefined ? propVal : "";
        changed = true;
        continue;
      }

      if (
        typeof localVal === "string" &&
        localVal === "" &&
        propVal !== undefined &&
        propVal !== "" &&
        JSON.stringify(propVal) !== JSON.stringify(localVal)
      ) {
        updates[key] = propVal;
        changed = true;
      }
    }

    if (changed) {
      isProgrammaticUpdate.current = true;
      setTemplateObject((prev) => ({ ...prev, ...updates }));
    }
  }, [initialTemplate, sampleMap, hideTemplateDesigner]);

  // Notify parent of template changes. Skipped when hideTemplateDesigner is
  // true (auth configs auto-fill the template in the parent's save handler).
  useEffect(() => {
    if (hideTemplateDesigner) return;
    if (onChangeRef.current && !isProgrammaticUpdate.current) {
      onChangeRef.current(templateObject);
    }
    isProgrammaticUpdate.current = false;
  }, [templateObject, hideTemplateDesigner]);

  // Update preview whenever sample or template changes
  useEffect(() => {
    const sampleJson = sampleMap[activeStatus] || "";
    let parsed = null;
    let error = null;
    try {
      parsed = JSON.parse(sampleJson);
      setJsonError(null);
      setParsedSample(parsed);
    } catch (err) {
      error = err.message;
      setJsonError(error);
      setParsedSample(null);
      setPreview(`JSON Error: ${error}`);
      return;
    }

    const activeTemplate = templateObject[activeStatus] || "";
    if (!activeTemplate.trim()) {
      setPreview("(No template for this status)");
      return;
    }

    const renderContext = { response_data: parsed };
    try {
      const rendered = nunjucks.renderString(activeTemplate, renderContext);
      setPreview(rendered);
    } catch (err) {
      setPreview(`Template Error: ${err.message}`);
    }
  }, [sampleMap, templateObject, activeStatus]);

  const flattenedFields = parsedSample
    ? flattenObject({ response_data: parsedSample })
    : {};

  const insertField = (fieldPath) => {
    const current = templateObject[activeStatus] || "";
    const newTemplate = current + `{{ ${fieldPath} }}`;
    setTemplateObject((prev) => ({ ...prev, [activeStatus]: newTemplate }));
  };

  const handleTemplateChange = (status, value) => {
    setTemplateObject((prev) => ({ ...prev, [status]: value }));
  };

  const handleSampleChange = (status, value) => {
    setSampleMap((prev) => ({ ...prev, [status]: value }));
  };

  const addStatus = () => {
    const newStatus = prompt("Enter HTTP status code (e.g., 200, 404):");
    if (!newStatus) return;
    const trimmed = newStatus.trim();
    if (!/^[1-5][0-9]{2}$/.test(trimmed)) {
      alert("Please enter a valid HTTP status code (e.g., 200, 404).");
      return;
    }
    if (templateObject[trimmed] !== undefined) {
      alert("Status already exists.");
      return;
    }
    setTemplateObject((prev) => ({ ...prev, [trimmed]: "" }));
    setSampleMap((prev) => ({ ...prev, [trimmed]: "" }));
    setInternalActiveStatus(trimmed);
    if (onActiveStatusChange) {
      onActiveStatusChange(trimmed);
    }
  };

  const removeStatus = (status) => {
    if (!window.confirm(`Remove status "${status}"?`)) return;
    const newTpl = { ...templateObject };
    delete newTpl[status];
    const newSamples = { ...sampleMap };
    delete newSamples[status];
    setTemplateObject(newTpl);
    setSampleMap(newSamples);
    if (activeStatus === status) {
      const keys = Object.keys(newTpl);
      const newStatus = keys.length > 0 ? keys[0] : "";
      setInternalActiveStatus(newStatus);
      if (onActiveStatusChange) {
        onActiveStatusChange(newStatus);
      }
    }
  };

  // Fetch ALL final responses (including default ones)
  const fetchAllFinalResponses = useCallback(async (statusCode) => {
    if (!statusCode) {
      setFinalResponses([]);
      return;
    }

    setLoadingResponses(true);
    try {
      const region = appData?.region?.toLowerCase() || "us";
      const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
      const token = localStorage.getItem("token") || "";
      const domainBase = process.env.REACT_APP_SL_BE_BASE_ENV_PART;

      const baseUrl = `https://${region}.autobot2.${domainBase}/${stackPrefix}/final_response_versions`;

      const entryTypes = [];

      if (configId) {
        entryTypes.push('local_app_level');
      }

      if (kbId) {
        entryTypes.push('default_app_level');
      }

      entryTypes.push('default_global_level');

      if (!configId && !kbId) {
        if (!entryTypes.includes('default_app_level')) entryTypes.push('default_app_level');
        if (!entryTypes.includes('local_app_level')) entryTypes.push('local_app_level');
      }

      const fetchPromises = entryTypes.map(async (entryType) => {
        const url = `${baseUrl}?entry_type=${entryType}&status_code=${statusCode}`;
        try {
          const response = await fetch(url, {
            headers: {
              "Authorization": token,
              "Content-Type": "application/json"
            }
          });
          if (response.ok) {
            const data = await response.json();
            return {
              type: entryType,
              items: data.items || [],
              success: true
            };
          }
          return { type: entryType, items: [], success: false };
        } catch (error) {
          console.error(`Error fetching ${entryType}:`, error);
          return { type: entryType, items: [], success: false };
        }
      });

      const results = await Promise.all(fetchPromises);

      const allItems = [];
      const seenIds = new Set();

      results.forEach(result => {
        result.items.forEach(item => {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            item._type = result.type;
            allItems.push(item);
          }
        });
      });

      const typeOrder = { local_app_level: 0, default_app_level: 1, default_global_level: 2 };
      allItems.sort((a, b) => (typeOrder[a._type] || 99) - (typeOrder[b._type] || 99));

      setFinalResponses(allItems);

    } catch (error) {
      console.error("Error fetching final responses:", error);
      setFinalResponses([]);
    } finally {
      setLoadingResponses(false);
    }
  }, [appData, configId, kbId]);

  // When activeStatus changes, fetch all final responses — skipped in
  // auth mode (no dropdown, nothing to fetch for).
  useEffect(() => {
    if (hideTemplateDesigner) return;
    if (activeStatus) {
      fetchAllFinalResponses(activeStatus);

      if (respData && respData[activeStatus]) {
        setSelectedReqUid(respData[activeStatus].req_uid || '');
      } else {
        setSelectedReqUid('');
      }
    }
  }, [activeStatus, hideTemplateDesigner]);

  // Handle req_uid selection with status and entry_type
  const handleReqUidSelect = useCallback((reqUid, template, entryType) => {
    const status = activeStatus;
    setSelectedReqUid(reqUid);

    if (template) {
      const templateStr = typeof template === 'object'
        ? JSON.stringify(template, null, 2)
        : template;
      handleTemplateChange(status, templateStr);
    }

    if (onReqUidSelect) {
      onReqUidSelect(status, reqUid, entryType || '');
    }
  }, [activeStatus, onReqUidSelect]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reqUidDropdownRef.current && !reqUidDropdownRef.current.contains(event.target)) {
        setReqUidDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const statusKeys =
    Object.keys(templateObject).length > 0
      ? Object.keys(templateObject)
      : Object.keys(sampleMap);

  useEffect(() => {
    if (
      activeStatus &&
      !statusKeys.includes(activeStatus) &&
      statusKeys.length > 0
    ) {
      const newStatus = statusKeys[0];
      setInternalActiveStatus(newStatus);
      if (onActiveStatusChange) {
        onActiveStatusChange(newStatus);
      }
    }
    if (!activeStatus && statusKeys.length > 0) {
      const newStatus = statusKeys[0];
      setInternalActiveStatus(newStatus);
      if (onActiveStatusChange) {
        onActiveStatusChange(newStatus);
      }
    }
  }, [statusKeys, activeStatus]);

  const getTypeLabel = (type) => {
    const labels = {
      local_app: '📱 Local App',
      local_app_all: '📱 Local App',
      default_app: '🌐 Default App',
      default_global: '🌍 Default Global'
    };
    return labels[type] || '📄 Template';
  };

  const getTypeColor = (type) => {
    const colors = {
      local_app: '#4a6cf7',
      local_app_all: '#4a6cf7',
      default_app: '#38a169',
      default_global: '#e53e3e'
    };
    return colors[type] || '#718096';
  };

  const getCurrentReqUidInfo = () => {
    if (respData && respData[activeStatus]) {
      return respData[activeStatus];
    }
    return null;
  };

  const currentReqUidInfo = getCurrentReqUidInfo();

  const handleViewAllTemplates = () => {
    if (onViewAllTemplates) {
      onViewAllTemplates();
    }
  };

  const handleStatusTabClick = (status) => {
    if (readOnly) return;
    setInternalActiveStatus(status);
    if (onActiveStatusChange) {
      onActiveStatusChange(status);
    }
  };

  return (
    <>
      <div className={styles.container}>
        {!hideTemplateDesigner && (
          <>
            <h4 className={styles.title}>Response Template (Optional)</h4>
            <p className={styles.description}>
              Define how the API response is converted into the chatbot message. Use
              Jinja syntax. You can provide different templates for different
              HTTP status codes (e.g., <code>200</code>, <code>404</code>).
            </p>
          </>
        )}

        <div className={styles.statusTabs}>
          {statusKeys.map((status) => (
            <button
              key={status}
              type="button"
              className={`${styles.statusTab} ${activeStatus === status ? styles.activeTab : ""}`}
              onClick={() => handleStatusTabClick(status)}
              disabled={readOnly}
            >
              {status}
              {/* Show the ✓ marker only in template-designer mode. */}
              {!hideTemplateDesigner &&
                respData &&
                respData[status] &&
                respData[status].req_uid && (
                  <span
                    style={{
                      marginLeft: "6px",
                      fontSize: "10px",
                      color: "#4CAF50",
                      backgroundColor: "#E8F5E9",
                      padding: "1px 6px",
                      borderRadius: "10px",
                    }}
                  >
                    ✓
                  </span>
                )}
              <span
                className={styles.removeStatus}
                onClick={(e) => {
                  e.stopPropagation();
                  removeStatus(status);
                }}
                disabled={readOnly}
              >
                ×
              </span>
            </button>
          ))}
          {!readOnly && (
            <button
              type="button"
              className={styles.addStatusButton}
              onClick={addStatus}
            >
              + Add Status
            </button>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Sample Response (JSON) for <code>{activeStatus || "status"}</code>{" "}
            status
          </label>
          <textarea
            value={sampleMap[activeStatus] || ""}
            onChange={(e) => handleSampleChange(activeStatus, e.target.value)}
            rows={4}
            className={`${styles.textarea} ${jsonError ? styles.textareaError : ""}`}
            placeholder='[{"room_type":"Standard","price_per_night":100}, …]  or  {"rooms": [{"room_type":"Standard",…}]}'
            disabled={readOnly}
          />
          {jsonError && (
            <div className={styles.errorMessage}>Invalid JSON: {jsonError}</div>
          )}
          <small className={styles.helperText}>
            The entire response is available as <code>response_data</code>. For
            arrays, iterate directly (
            <code>{"{% for item in response_data %}"}</code>). For objects, access
            keys like <code>response_data.rooms</code>.
          </small>
        </div>

        {/* Template-designer-only UI. Everything below is hidden for auth
            configs (hideTemplateDesigner === true). */}
        {!hideTemplateDesigner && (
          <>
            {parsedSample && Object.keys(flattenedFields).length > 0 && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Insert field:</label>
                <div className={styles.fieldButtons}>
                  {Object.keys(flattenedFields).map((field) => (
                    <button
                      key={field}
                      type="button"
                      onClick={() => insertField(field)}
                      className={styles.fieldButton}
                      disabled={readOnly}
                    >
                      {field}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.formGroup}>
              <div className={styles.sampleWrap}>
                <label className={styles.resplabel}>
                  <strong>📋 Load Saved Response Template</strong>
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "12px",
                      color: "#666",
                      fontWeight: "normal",
                    }}
                  >
                    (Choose from previously created templates for this status)
                  </span>
                </label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {!readOnly && onViewAllTemplates && (
                    <button
                      type="button"
                      onClick={handleViewAllTemplates}
                      style={{
                        padding: "6px 14px",
                        backgroundColor: "#38a169",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      title="View all templates for this API Config"
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#2f855a")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#38a169")
                      }
                    >
                      <span>📋</span> View All Templates
                    </button>
                  )}
                </div>
              </div>

              <div
                ref={reqUidDropdownRef}
                style={{ position: "relative", marginTop: "1rem" }}
              >
                <button
                  type="button"
                  onClick={() => setReqUidDropdownOpen(!reqUidDropdownOpen)}
                  className={styles.dropdownToggle}
                  disabled={readOnly || loadingResponses}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {currentReqUidInfo?.req_uid
                      ? `✅ Loaded: ${currentReqUidInfo.req_uid.substring(0, 20)}... (${currentReqUidInfo.entry_type || "unknown"})`
                      : loadingResponses
                        ? "⏳ Loading saved templates..."
                        : finalResponses.length > 0
                          ? `📥 Select a saved template (${finalResponses.length} available)`
                          : "📭 No saved templates for this status"}
                  </span>
                  <span>{reqUidDropdownOpen ? "▲" : "▼"}</span>
                </button>

                {reqUidDropdownOpen && finalResponses.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      maxHeight: "300px",
                      overflowY: "auto",
                      zIndex: 1000,
                      marginTop: "2px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 12px",
                        borderBottom: "1px solid #eee",
                        fontSize: "12px",
                        color: "#666",
                        backgroundColor: "#fafafa",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        {finalResponses.length} saved template(s) available
                      </span>
                      <span style={{ fontSize: "10px", color: "#999" }}>
                        {
                          finalResponses.filter((r) => r._type === "local_app")
                            .length
                        }{" "}
                        Local ·
                        {
                          finalResponses.filter((r) => r._type === "default_app")
                            .length
                        }{" "}
                        Default App ·
                        {
                          finalResponses.filter(
                            (r) => r._type === "default_global",
                          ).length
                        }{" "}
                        Global
                      </span>
                    </div>
                    {finalResponses.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => {
                          handleReqUidSelect(
                            item.id,
                            item.resp_template,
                            item._type,
                          );
                          setReqUidDropdownOpen(false);
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderBottom: "1px solid #f0f0f0",
                          fontSize: "13px",
                          transition: "background-color 0.2s",
                          borderLeft: `3px solid ${getTypeColor(item._type)}`,
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#f0f4ff")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "white")
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              fontSize: "12px",
                              color: "#333",
                            }}
                          >
                            ID: {item.id.substring(0, 12)}...
                          </div>
                          <span
                            style={{
                              fontSize: "10px",
                              fontWeight: "600",
                              color: getTypeColor(item._type),
                              backgroundColor: `${getTypeColor(item._type)}15`,
                              padding: "2px 8px",
                              borderRadius: "10px",
                              border: `1px solid ${getTypeColor(item._type)}30`,
                            }}
                          >
                            {getTypeLabel(item._type)}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#666",
                            marginTop: "2px",
                          }}
                        >
                          {item.kb_id || "Global Template"}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#999",
                            marginTop: "2px",
                          }}
                        >
                          Config:{" "}
                          {item.config_id?.substring(0, 12) || "N/A"}... |
                          Status: {item.status_code}
                        </div>
                        {item.resp_template && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#555",
                              marginTop: "4px",
                              backgroundColor: "#f9f9f9",
                              padding: "4px 8px",
                              borderRadius: "3px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%",
                            }}
                          >
                            Template:{" "}
                            {typeof item.resp_template === "object"
                              ? JSON.stringify(item.resp_template).substring(
                                  0,
                                  60,
                                ) + "..."
                              : item.resp_template.substring(0, 60) + "..."}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {reqUidDropdownOpen &&
                  finalResponses.length === 0 &&
                  !loadingResponses && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "white",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        padding: "12px",
                        textAlign: "center",
                        color: "#999",
                        zIndex: 1000,
                        marginTop: "2px",
                      }}
                    >
                      No saved templates found for status {activeStatus}
                      <div
                        style={{
                          fontSize: "11px",
                          marginTop: "4px",
                          color: "#bbb",
                        }}
                      >
                        Use the "🎨 Create for {activeStatus}" button below to
                        design one
                      </div>
                    </div>
                  )}
              </div>

              {currentReqUidInfo?.req_uid && (
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#4CAF50",
                  }}
                >
                  ✅ Template loaded successfully
                  <span
                    style={{
                      marginLeft: "8px",
                      fontSize: "11px",
                      color: "#666",
                    }}
                  >
                    ({currentReqUidInfo.entry_type || "unknown"} level)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      handleReqUidSelect("", "", "");
                    }}
                    style={{
                      marginLeft: "10px",
                      padding: "2px 8px",
                      fontSize: "11px",
                      backgroundColor: "#ff4444",
                      color: "white",
                      border: "none",
                      borderRadius: "3px",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
              <small className={styles.helperText}>
                💡 Select a saved template to load it into the editor.
                Templates are organized by scope:
                <span style={{ color: "#4a6cf7" }}>
                  {" "}
                  Local (this app only)
                </span>
                ,
                <span style={{ color: "#38a169" }}>
                  {" "}
                  Default (shared across the app)
                </span>
                , and
                <span style={{ color: "#e53e3e" }}>
                  {" "}
                  Global (shared across all apps)
                </span>
                .
              </small>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ResponseTemplateEditor;